import React, { FC, useState } from 'react';
import { TextButton } from '../../../../widgets/buttons';
import { Icon } from '../../../../widgets/icon';
import { Segmented } from '../../../../widgets/segmented';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import SpaceDashboardIcon from 'src/shared/material_icons/action/space_dashboard.svg';
import SpeedIcon from 'src/shared/material_icons/av/speed.svg';
import TouchAppIcon from 'src/shared/material_icons/action/touch_app.svg';
import ViewInArIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import { VtoIcon } from '../../../../widgets/icons/scene';
import LightModeIcon from 'src/shared/material_icons/device/light_mode.svg';
import DarkModeIcon from 'src/shared/material_icons/device/dark_mode.svg';
import ComputerIcon from 'src/shared/material_icons/hardware/computer.svg';
import ViewColumnIcon from 'src/shared/material_icons/action/view_column.svg';
import ViewSidebarIcon from 'src/shared/material_icons/action/view_sidebar.svg';
import MenuIcon from 'src/shared/material_icons/navigation/menu.svg';
import MenuOpenIcon from 'src/shared/material_icons/navigation/menu_open.svg';
import AREditor from './scene-editors/arEditor';
import SettingsEditor from './scene-editors/settingsEditor';
import VTOEditor from './scene-editors/vtoEditor';
import { WorkspacePreferenceKey, WorkspacePreferences } from '../configurator-scene-editor/useWorkspacePreferences';
import { SelectionHighlight } from '../configurator-scene-editor/selectionHighlight';
import { PropertiesPanelSwitch } from './scene-properties-panel/propertiesPanelSwitchProperty';
import { PropertiesPanelColorControl } from './scene-properties-panel/propertiesPanelColorProperty';
import { SettingControl, SettingLabel, SettingRow, SettingsSection, SettingsSectionTitle } from './sceneEditorSettingsLayout';
import { SHOW_NOT_YET_AVAILABLE } from './scene-editors/notYetAvailable';

// AR E VTO SONO GRUPPI LORO (utente, 2026-09-25): stavano dentro Behaviours come due righe che
// aprivano una sotto-pagina con «Back», cioè un secondo livello di navigazione dentro la finestra.
// Ora si raggiungono dalla colonna come gli altri, e Composer non c'è più: la sua unica voce,
// il generatore AI, è un comportamento e vive in Behaviours.
type SettingsCategory = 'interface' | 'rendering' | 'behaviours' | 'ar' | 'vto';

// CHI APRE LA FINESTRA DECIDE COSA SI VEDE (utente, 2026-09-25). Performance, AR, VTO e Info points
// parlano della scena 3D e hanno senso solo nello Scene Editor; dal Composer restano le preferenze
// dell'interfaccia e il generatore AI.
export type SettingsHost = 'sceneEditor' | 'composer';

const SCENE_ONLY_CATEGORIES: SettingsCategory[] = ['rendering', 'ar', 'vto'];

interface SceneEditorSettingsDialogProps {
    host: SettingsHost;
    preferences: WorkspacePreferences;
    setPreference: <K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => void;
    /** Lo passa solo lo Scene Editor, che tiene l'alone: senza, la sezione Viewport non c'è. */
    selectionHighlight?: SelectionHighlight;
    onSelectionHighlightChange?: (changes: Partial<SelectionHighlight>) => void;
    /**
     * Rimette le colonne alle misure di fabbrica. Non è fra le scelte qui sopra perché non è una
     * scelta: è un rimedio, e come tale non ha uno stato da mostrare — si preme e basta. E non passa
     * da «Cancel»: chi lo preme sta già disfacendo.
     */
    onResetPanelSizes?: () => void;
    sceneEditor: any;
    zkScene: any;
    zkProduct: any;
    additionalFeatures?: any;
    /**
     * QUI DENTRO SI TOCCA LA SCENA, e la scena va segnata da salvare.
     *
     * Chi tiene il pennarello ascolta gli eventi dentro l'area di lavoro, ma questa finestra si
     * disegna in un portale attaccato alla radice della pagina: quello che si cambia qui non
     * passa mai di lì. Senza questa richiamata, spegnere l'AR cambiava davvero la
     * configurazione e poi «Save» rispondeva «nulla da salvare» — il valore restava vivo fino
     * al ricaricamento e non arrivava mai al server.
     */
    onSceneChanged?: () => void;
}

// Non disegna: ascolta. `display: contents` lo toglie dal disegno, così le sezioni restano
// figlie di `Content` come prima e nessun layout si accorge che è passato di qui.
const SceneChanges = styled.div`
    display: contents;
`;

// NIENTE MARGINI NEGATIVI. La griglia usciva di 20px per lato per toccare i bordi della finestra,
// ma il corpo della dialog scorre: quei 40px in più erano la barra di scorrimento orizzontale.
const DialogLayout = styled.div`
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    min-height: 460px;
    overflow: hidden;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

const CategoryList = styled.nav`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 16px 16px 16px 0;
    border-right: 1px solid ${(props) => props.theme.app.borderColor};
`;

const ItemButton = styled.button<{ $active?: boolean }>`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-radius: 8px;
    text-align: left;
    color: ${(props) => (props.$active ? props.theme.app.textColor : props.theme.app.primary)};
    background: ${(props) => (props.$active ? props.theme.app.backgroundToolbar : 'transparent')};
    cursor: pointer;
    transition: background 140ms ease;

    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.app.textColor};
        background: ${(props) => (props.$active ? props.theme.app.backgroundToolbar : props.theme.app.pageBackgroundColor)};
        outline: none;
    }
`;

const ItemIcon = styled(Icon)`
    width: 20px;
    height: 20px;
    min-width: 20px;

    svg {
        width: 20px;
        height: 20px;
    }
`;

const ItemText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

const ItemTitle = styled.span`
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
    font-weight: 600;
`;

const ItemSubtitle = styled.span`
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
`;

const Content = styled.div`
    min-width: 0;
    padding: 20px 0 20px 24px;
    overflow-x: hidden;
    overflow-y: auto;
`;

const InterfaceSections = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const CATEGORIES: { key: SettingsCategory; icon: React.ReactNode; title: string; subtitle: string }[] = [
    {
        key: 'interface',
        icon: <SpaceDashboardIcon />,
        title: T._('Interface', 'Admin'),
        subtitle: SHOW_NOT_YET_AVAILABLE ? T._('Theme, panel layout', 'Admin') : T._('Panel layout', 'Admin')
    },
    // La CHIAVE resta `rendering` — è la parola tecnica, e la usano il pannello e la sua `section`;
    // cambia l'etichetta, che è quella del merchant. «Performance» dice cosa si sta scegliendo
    // davvero qui: quanto costa caricare questa scena.
    { key: 'rendering', icon: <SpeedIcon />, title: T._('Performance', 'Admin'), subtitle: T._('Textures, loading', 'Admin') },
    { key: 'behaviours', icon: <TouchAppIcon />, title: T._('Behaviours', 'Admin'), subtitle: T._('AI configuration', 'Admin') },
    { key: 'ar', icon: <ViewInArIcon />, title: T._('Augmented Reality', 'Admin'), subtitle: T._('Placement, real-world scale, conversion', 'Admin') },
    { key: 'vto', icon: <VtoIcon />, title: T._('Virtual Try-On', 'Admin'), subtitle: T._('Type, options, reference files', 'Admin') }
];

const SceneEditorSettingsDialog: FC<SceneEditorSettingsDialogProps> = ({
    host,
    preferences,
    setPreference,
    selectionHighlight,
    onSelectionHighlightChange,
    onResetPanelSizes,
    sceneEditor,
    zkScene,
    zkProduct,
    additionalFeatures,
    onSceneChanged
}) => {
    const [category, setCategory] = useState<SettingsCategory>('interface');
    const isSceneEditor = host === 'sceneEditor';
    const categories = isSceneEditor ? CATEGORIES : CATEGORIES.filter((item) => !SCENE_ONLY_CATEGORIES.includes(item.key));
    // Il gestore dei dialoghi conserva il nodo così com'è stato creato: le props non
    // tornano più. Lo stato corrente dei segmented sta quindi qui, e ogni scelta viene
    // comunque applicata subito fuori — un tema lo si giudica vedendolo. È «Cancel», in
    // fondo alla finestra, a rimettere le cose com'erano davvero.
    const [values, setValues] = useState(preferences);

    const choose = <K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => {
        setValues((current) => ({ ...current, [key]: value }));
        setPreference(key, value);
    };

    const [highlight, setHighlight] = useState(selectionHighlight);

    const changeHighlight = (changes: Partial<SelectionHighlight>) => {
        setHighlight((current) => current && { ...current, ...changes });
        onSelectionHighlightChange?.(changes);
    };

    // Tre righe, nell'ordine del prototipo. L'unità di misura non è qui perché vive nella
    // barra di stato sotto il palco, dove la si legge; i pannelli chiusi non sono qui perché
    // ogni pannello ha già il suo comando di chiusura, e una preferenza in due posti è una
    // preferenza che si contraddice.
    //
    // SOTTO, IL PALCO: l'alone della selezione. Nei vecchi editor stava nella toolbar; qui è una
    // preferenza di chi guarda come le tre sopra, e il colore ha senso solo ad alone acceso.
    const renderInterface = () => (
        <InterfaceSections>
            <SettingsSection>
                <SettingsSectionTitle>{T._('Appearance', 'Admin')}</SettingsSectionTitle>
                {/* IL TEMA NON SI APPLICA ANCORA: la scelta si salva, ma nessuna delle due stanze ha una veste
                    scura. Resta scritta e spenta finché non c'è — vedi `SHOW_NOT_YET_AVAILABLE`. */}
                {SHOW_NOT_YET_AVAILABLE && (
                    <SettingRow>
                        <SettingLabel>{T._('Theme', 'Admin')}</SettingLabel>
                        <Segmented
                            aria-label={T._('Interface theme', 'Admin')}
                            value={values.colorScheme}
                            onChange={(value) => choose('colorScheme', value)}
                            options={[
                                {
                                    value: 'light',
                                    icon: (
                                        <Icon>
                                            <LightModeIcon />
                                        </Icon>
                                    ),
                                    label: T._('Light', 'Admin')
                                },
                                {
                                    value: 'dark',
                                    icon: (
                                        <Icon>
                                            <DarkModeIcon />
                                        </Icon>
                                    ),
                                    label: T._('Dark', 'Admin')
                                },
                                {
                                    value: 'system',
                                    icon: (
                                        <Icon>
                                            <ComputerIcon />
                                        </Icon>
                                    ),
                                    label: T._('Same as your computer', 'Admin')
                                }
                            ]}
                        />
                    </SettingRow>
                )}
                {/* La colonna delle proprietà e la libreria sotto il palco sono dello Scene Editor: il Composer
                    non le ha, e la scelta di dove metterle lì non sposterebbe niente. */}
                {isSceneEditor && (
                    <SettingRow>
                        <SettingLabel>{T._('Properties panel', 'Admin')}</SettingLabel>
                        <Segmented
                            aria-label={T._('Properties panel position', 'Admin')}
                            value={values.propertiesPanel}
                            onChange={(value) => choose('propertiesPanel', value)}
                            options={[
                                {
                                    value: 'side',
                                    icon: (
                                        <Icon>
                                            <ViewColumnIcon />
                                        </Icon>
                                    ),
                                    label: T._('Properties beside the list', 'Admin')
                                },
                                {
                                    value: 'right',
                                    icon: (
                                        <Icon>
                                            <ViewSidebarIcon />
                                        </Icon>
                                    ),
                                    label: T._('Properties on the right · materials move under the model', 'Admin')
                                }
                            ]}
                        />
                    </SettingRow>
                )}
                <SettingRow>
                    <SettingLabel>{T._('Section rails', 'Admin')}</SettingLabel>
                    <Segmented
                        aria-label={T._('Section rails', 'Admin')}
                        value={values.sectionRails}
                        onChange={(value) => choose('sectionRails', value)}
                        options={[
                            {
                                value: 'compact',
                                icon: (
                                    <Icon>
                                        <MenuIcon />
                                    </Icon>
                                ),
                                label: T._('Compact rails · icons only', 'Admin')
                            },
                            {
                                value: 'expanded',
                                icon: (
                                    <Icon>
                                        <MenuOpenIcon />
                                    </Icon>
                                ),
                                label: T._('Expanded rails · icons with names', 'Admin')
                            }
                        ]}
                    />
                </SettingRow>
                {onResetPanelSizes && (
                    <SettingRow>
                        <SettingLabel>{T._('Panel sizes', 'Admin')}</SettingLabel>
                        <TextButton onClick={onResetPanelSizes}>{T._('Reset to default', 'Admin')}</TextButton>
                    </SettingRow>
                )}
            </SettingsSection>
            {highlight && (
                <SettingsSection>
                    <SettingsSectionTitle>{T._('Viewport', 'Admin')}</SettingsSectionTitle>
                    <SettingRow>
                        <SettingLabel id='settings-selection-highlight-label'>{T._('Selection highlight', 'Admin')}</SettingLabel>
                        <PropertiesPanelSwitch
                            id='settings-selection-highlight'
                            checked={highlight.enabled}
                            labelledBy='settings-selection-highlight-label'
                            onChange={(enabled) => changeHighlight({ enabled })}
                        />
                    </SettingRow>
                    <SettingRow>
                        <SettingLabel htmlFor='settings-highlight-color'>{T._('Highlight color', 'Admin')}</SettingLabel>
                        <SettingControl>
                            <PropertiesPanelColorControl
                                id='settings-highlight-color'
                                value={highlight.color}
                                disabled={!highlight.enabled}
                                onChange={(color) => changeHighlight({ color })}
                            />
                        </SettingControl>
                    </SettingRow>
                </SettingsSection>
            )}
        </InterfaceSections>
    );

    return (
        <DialogLayout>
            <CategoryList aria-label={T._('Settings groups', 'Admin')}>
                {categories.map((item) => (
                    <ItemButton
                        key={item.key}
                        type='button'
                        $active={category === item.key}
                        aria-current={category === item.key ? 'page' : undefined}
                        onClick={() => setCategory(item.key)}
                    >
                        <ItemIcon>{item.icon}</ItemIcon>
                        <ItemText>
                            <ItemTitle>{item.title}</ItemTitle>
                            <ItemSubtitle>
                                {item.key === 'behaviours' && isSceneEditor ? T._('AI configuration, info points', 'Admin') : item.subtitle}
                            </ItemSubtitle>
                        </ItemText>
                        <Icon>
                            <ChevronRightIcon />
                        </Icon>
                    </ItemButton>
                ))}
            </CategoryList>
            <Content>
                {/* L’INTERFACCIA STA FUORI DALL’ASCOLTO, e non per dimenticanza: tema e disposizione
                    dei pannelli sono preferenze di chi guarda, non dati della scena. Segnarle da
                    salvare accenderebbe «modifiche non salvate» per aver cambiato colore allo sfondo. */}
                {category === 'interface' && renderInterface()}
                <SceneChanges onChangeCapture={onSceneChanged} onInputCapture={onSceneChanged}>
                    {category === 'rendering' && (
                        <SettingsEditor section='rendering' sceneEditor={sceneEditor} zkScene={zkScene} onSceneChanged={onSceneChanged} />
                    )}
                    {category === 'behaviours' && (
                        <SettingsEditor
                            section='behaviours'
                            sceneEditor={sceneEditor}
                            zkScene={zkScene}
                            isAIEnabled={zkProduct?.get?.('isAiConfigurationEnabled')}
                            setAIEnabled={(enabled) => zkProduct?.set?.('isAiConfigurationEnabled', enabled)}
                            additionalFeatures={additionalFeatures}
                            showInfoPoints={isSceneEditor}
                            onSceneChanged={onSceneChanged}
                        />
                    )}
                    {category === 'ar' && <AREditor sceneEditor={sceneEditor} zkScene={zkScene} onSceneChanged={onSceneChanged} />}
                    {category === 'vto' && (
                        <VTOEditor productModelID={zkProduct?.get?.('modelID')} additionalFeatures={additionalFeatures} onSceneChanged={onSceneChanged} />
                    )}
                </SceneChanges>
            </Content>
        </DialogLayout>
    );
};

export default SceneEditorSettingsDialog;
